/**
 * Hand-rolled harvest-photo client.
 *
 * orval (axios JSON mode) can't model multipart upload or a raw-bytes stream,
 * so these three endpoints are hand-rolled with the built-in `fetch` + FormData.
 *
 * We deliberately mirror the base-URL + access-token resolution that
 * `src/api/client.ts` uses (env `VITE_FSE_API_URL` → prod VPS fallback;
 * access token in localStorage under `fse_access_token`) rather than exporting
 * and importing those internals — they're module-private in client.ts and the
 * contract is tiny + stable. If client.ts ever changes the key/fallback, keep
 * these two constants in sync.
 *
 * Backend contract (DEPLOYED, owner-scoped + farmer-only; mutations also
 * independent-only — coop members get 403):
 *  - POST   /harvests/:id/photos          multipart, field `file`, image/* ≤5 MB → HarvestPhotoDto
 *  - GET    /harvests/:id/photos          → { data: HarvestPhotoDto[] } (newest first)
 *  - GET    /harvests/:id/photos/:photoId raw image bytes (needs Authorization)
 *
 * The GET-by-id endpoint streams bytes behind auth, so it can't go straight
 * into `<img src>`; callers fetch it with the bearer header, turn the response
 * into a blob, and mint an object URL (which they must revoke on cleanup).
 */

// Kept in sync with src/api/client.ts (module-private there).
const DEFAULT_BASE_URL = 'https://api.foodsecurityexchange.cloud';
const ACCESS_KEY = 'fse_access_token';

function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_FSE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return DEFAULT_BASE_URL;
}

function readAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACCESS_KEY);
}

function authHeader(): Record<string, string> {
  const token = readAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface HarvestPhotoDto {
  id: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

/** Best-effort extraction of a server error message for thrown errors. */
async function describeError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: unknown };
    if (body && typeof body === 'object' && 'message' in body) {
      return String(body.message);
    }
  } catch {
    // Non-JSON body (e.g. an HTML error page) — fall through to status text.
  }
  return `${res.status} ${res.statusText}`;
}

/**
 * Upload a single image to a harvest. The browser sets the multipart
 * `Content-Type` boundary itself, so we must NOT set Content-Type here.
 */
export async function uploadHarvestPhoto(
  harvestId: string,
  file: File,
): Promise<HarvestPhotoDto> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${resolveBaseUrl()}/harvests/${harvestId}/photos`, {
    method: 'POST',
    headers: { ...authHeader() },
    body: fd,
  });
  if (!res.ok) throw new Error(await describeError(res));
  return (await res.json()) as HarvestPhotoDto;
}

/** List a harvest's photos (newest first). */
export async function listHarvestPhotos(
  harvestId: string,
): Promise<HarvestPhotoDto[]> {
  const res = await fetch(`${resolveBaseUrl()}/harvests/${harvestId}/photos`, {
    method: 'GET',
    headers: { Accept: 'application/json', ...authHeader() },
  });
  if (!res.ok) throw new Error(await describeError(res));
  const body = (await res.json()) as { data: HarvestPhotoDto[] };
  return body.data;
}

/**
 * Fetch the raw image bytes for one photo with auth and turn it into an object
 * URL suitable for `<img src>`. The caller owns the URL and must
 * `URL.revokeObjectURL` it on cleanup to avoid leaking blobs.
 */
export async function fetchHarvestPhotoObjectUrl(
  harvestId: string,
  photoId: string,
): Promise<string> {
  const res = await fetch(
    `${resolveBaseUrl()}/harvests/${harvestId}/photos/${photoId}`,
    { method: 'GET', headers: { ...authHeader() } },
  );
  if (!res.ok) throw new Error(await describeError(res));
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
